/** **************************************************************************************
 * Copyright (c) 2022-2023. Peter Lemke, SV-Studios                                     *
 * This File, its contents and by extension the corresponding project is property of    *
 * SV-Studios and may not be used without explicit permission to do so.                 *
 *                                                                                      *
 * management@sv-studios.net                                                            *
 *************************************************************************************** */

import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import CookieDialog from "../components/Dialog/CookieDialog";
import config, { devModeEnabled } from "../config";
import Logger from "../Logger";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../index";
import { loadAndInitializeToggles } from "../store/flags/flags-actions";
import FlagsDialog from "../components/Dialog/FlagsDialog";
import MDButton from "../components/MDButton";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import * as jose from "jose";
import { cookieActions } from "../store/cookie/cookie-slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightFromBracket, faRobot, faXmark } from "@fortawesome/pro-solid-svg-icons";
import Cookies from "js-cookie";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

const loggerOptions = {
  module: "CookieHandler",
};

const initializeSentry = () => {
  const replay = Sentry.replayIntegration({
    maskAllText: true,
    blockAllMedia: true,
  });

  const router = Sentry.reactRouterV6BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  });
  const tracing = Sentry.browserTracingIntegration();
  const profiling = Sentry.browserProfilingIntegration();
  const feedback = Sentry.feedbackIntegration({
    colorScheme: "system",
  });

  const tags = {
    version: config.version,
    theme: window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
  };

  try {
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: devModeEnabled() ? "development" : "production",
      initialScope: {
        tags,
      },
      integrations: [replay, router, tracing, profiling, feedback],
      normalizeDepth: 10,
      profilesSampleRate: devModeEnabled() ? 0.75 : 0.1,
      release: config.version !== "development" ? `panel@${config.version}` : undefined,
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      tracePropagationTargets: [
        "localhost",
        "panel.sv-studios.net",
        /^\//,
        /^(?:(https|http)?:\/\/)?panel.sv-studios\.net\/api(\/.*)?$/gi,
      ],
      tracesSampleRate: devModeEnabled() ? 0.75 : 0.1,
    });
    Logger.info("Sentry initialized", loggerOptions);
  } catch (e) {
    Logger.warning("Error while initializing Sentry", loggerOptions, e);
  }
};

export const CookieHandler = () => {
  const sentryInitializedRef = useRef(false);
  const { required, analytics, preferences, supportPin, supportPinData } = useSelector(
    (state) => state.cookie
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (analytics && !devModeEnabled()) {
      if (!sentryInitializedRef.current) {
        initializeSentry();
        sentryInitializedRef.current = true;
      }
      Sentry.getReplay().start();
      Logger.info("Analytics cookie accepted", loggerOptions);
    } else {
      Logger.info("Analytics cookie declined", loggerOptions);
      Sentry.close().then(() => {
        Logger.debug("Flush Sentry Core", loggerOptions);
      });
      Sentry.flush().then(() => {
        Logger.debug("Flush Sentry Transport", loggerOptions);
      });
      Sentry.getReplay()
        ?.flush({
          continueRecording: false,
        })
        .then(() => {
          Logger.debug("Flush Sentry Replay", loggerOptions);
        });
      Sentry.getReplay()
        ?.stop()
        .then(() => {
          Logger.debug("Flush Sentry Replay", loggerOptions);
        });
    }
  }, [analytics]);

  useEffect(() => {
    if (preferences) {
      Logger.info("Preferences cookie accepted", loggerOptions);

      client
        .start()
        .then(() => {
          Logger.debug("Unleash Client started", loggerOptions);
          dispatch(loadAndInitializeToggles());
        })
        .catch((e) => {
          Logger.warning("Error while starting Unleash Client", loggerOptions, e);
        });
    } else {
      Logger.info("Preferences cookie declined", loggerOptions);
      client.stop();
      Logger.debug("Unleash Client stopped", loggerOptions);
    }
  }, [preferences]);

  useEffect(() => {
    if (supportPin) {
      Logger.info("Support Pin cookie found", loggerOptions);
      const jwt = jose.decodeJwt(supportPin);

      dispatch(
        cookieActions.setSupportPinData({
          botId: jwt?.botId,
          expiresAt: jwt?.exp * 1000,
        })
      );
    } else {
      dispatch(cookieActions.setSupportPinData(null));
    }
  }, [supportPin]);

  useEffect(() => {
    if (supportPinData) {
      const action = (snackbarId) => (
        <>
          <MDButton
            variant="text"
            size="medium"
            color="white"
            sx={{ mr: 1 }}
            iconOnly
            onClick={() => {
              enqueueSnackbar("Hinzugefügter Bot: " + supportPinData.botId, {
                anchorOrigin: { horizontal: "center", vertical: "top" },
                preventDuplicate: true,
                autoHideDuration: 10000,
                action: (snackbarId) => (
                  <MDButton
                    variant="text"
                    size="medium"
                    iconOnly
                    color="white"
                    onClick={() => {
                      closeSnackbar(snackbarId);
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} size="lg" />
                  </MDButton>
                ),
              });
            }}
          >
            <FontAwesomeIcon icon={faRobot} size="lg" />
          </MDButton>
          <MDButton
            variant="text"
            size="medium"
            iconOnly
            color="white"
            onClick={() => {
              closeSnackbar(snackbarId);
              Cookies.remove("support_pin");
              dispatch(cookieActions.setSupportPin(null));
              enqueueSnackbar("Du befindest dich nun im normalen Modus", {
                anchorOrigin: { horizontal: "center", vertical: "top" },
              });
            }}
          >
            <FontAwesomeIcon icon={faRightFromBracket} size="lg" />
          </MDButton>
        </>
      );

      enqueueSnackbar("Support-Modus", {
        anchorOrigin: { horizontal: "center", vertical: "top" },
        variant: "info",
        persist: true,
        preventDuplicate: true,
        autoHideDuration: supportPinData.expiresAt - Date.now(),
        action,
      });
    }
  }, [supportPinData]);

  return (
    <>
      <CookieDialog />
      <FlagsDialog />
    </>
  );
};
